<template>
  <section class="xiaomi-buds">
    <Heading
      tag="h2"
      size="lg"
      text-uppercase
      font-weight="ultra"
      align="center"
      spaces-bottom
      class="xiaomi-buds__heading"
    >
      Xiaomi Redmi Buds: Klangvolles Hörerlebnis, überall
    </Heading>
    <Paragraph
      class="xiaomi-buds__copy"
      align="center"
      is-size-big
      >Erlebe Musik und Anrufe in kristallklarer Qualität mit den Xiaomi Redmi Buds. Diese
      kabellosen Ohrhörer bieten einen dynamischen Klang und eine bequeme Passform für den ganzen
      Tag. Mit der neuesten Bluetooth-Technologie und einer langen Akkulaufzeit sind die Redmi Buds
      der perfekte Begleiter für unterwegs. Tauche ein in deine Lieblingsmusik, egal wo du bist, mit
      den Xiaomi Redmi Buds.</Paragraph
    >
    <div class="xiaomi-buds__buds-wrapper">
      <nuxt-link
        v-for="item in budsWithOffers"
        :key="item.label"
        :to="`/geraete/${item.offer?.product?.slug}`"
        class="xiaomi-buds__link btn btn--no-style"
        :class="`xiaomi-buds__link--${slugify(item.label)}`"
      >
        <div class="xiaomi-buds__item-image-wrapper">
          <div
            class="xiaomi-buds__item-image"
            :class="{
              'xiaomi-buds__item-image--before': item.images.hover?.src,
            }"
          >
            <CdnImage
              alt=""
              :src="item.images.initial.background"
              class="xiaomi-buds__item-image-background"
              lazy
            />
            <CdnImage
              :alt="item.label"
              :src="item.images.initial.visual"
              class="xiaomi-buds__item-image-visual"
              lazy
            />
          </div>
          <CdnImage
            v-if="item.images.hover?.src"
            :alt="item.label"
            :src="item.images.hover.src"
            class="xiaomi-buds__item-image xiaomi-buds__item-image--after"
            lazy
          />
        </div>
        <span class="xiaomi-buds__title">
          {{ item.label }}
          <span class="xiaomi-buds__arrow-symbol">
            <Arrow
              direction="right"
              tag="span"
            />
          </span>
        </span>
      </nuxt-link>
    </div>
    <LinkButton
      tag="nuxt-link"
      to="/zubehoer/kopfhoerer/_xiaomi"
      transparent
      >Alle Kopfhörer im Überblick</LinkButton
    >
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  Arrow,
  Heading,
  Paragraph,
  LinkButton,
  slugify,
} from '@i22-td-smarthome/component-library';
import CdnImage from '@/components/cdn-image.vue';

const props = defineProps({
  buds: {
    type: Array,
    default: () => [],
  },
});

const budsWithOffers = computed(() => props.buds.filter((item: any) => item.offer));
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.xiaomi-buds {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-inline: $grid-base;

  &__heading {
    @include mq($mq-md) {
      margin-bottom: 2 * $grid-base;
    }
  }

  &__copy {
    max-width: 1264px;
    margin-inline: auto;
    margin-bottom: 3 * $grid-base;

    @include mq($mq-md) {
      margin-bottom: 4 * $grid-base;
    }
  }

  &__buds-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3 * $grid-base;
    margin-bottom: 3 * $grid-base;

    @include mq($mq-md) {
      flex-direction: row;
      column-gap: 2 * $grid-base;
      flex-wrap: wrap;
      max-width: 990px;
    }

    @include mq($mq-xl) {
      max-width: unset;
      justify-content: center;
    }
  }

  &__link {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $grid-base;
    white-space: normal;
    text-align: left;
    width: 100%;
    max-width: 485px;

    @include mq($mq-md) {
      max-width: calc(50% - 12px);
    }

    @include mq($mq-xl) {
      max-width: 336px;
    }

    &:hover,
    &:focus {
      color: var(--color-font);
    }

    &:hover {
      .xiaomi-buds__item-image {
        &-wrapper {
          border-radius: 2 * $grid-base;
        }

        &--before {
          display: none;
        }

        &--after {
          position: relative;
          visibility: visible;
          width: 100%;
          height: 100%;
        }
      }

      .xiaomi-buds__arrow-symbol {
        &::before {
          min-width: $grid-base;
        }
      }
    }

    &--redmi-buds-5-pro {
      order: 1;

      .xiaomi-buds__item-image-visual {
        @include mq($mq-md) {
          width: 360px;
        }
      }
    }

    &--redmi-buds-5 {
      order: 2;

      @include mq($mq-md, $mq-xl - 1) {
        order: 3;
      }
    }

    &--redmi-buds-4-lite {
      order: 3;

      @include mq($mq-md, $mq-xl - 1) {
        order: 4;
      }
    }

    &--redmi-buds-4-active {
      order: 4;

      @include mq($mq-md, $mq-xl - 1) {
        order: 2;
      }

      .xiaomi-buds__item-image-visual {
        @include mq($mq-md) {
          width: 350px;
        }
      }
    }
  }

  &__item-image {
    width: 100%;
    height: 100%;
    position: relative;

    :deep(img) {
      height: 100%;
      width: 100%;
      object-fit: cover;
      object-position: center;
    }

    &-wrapper {
      overflow: hidden;
      transition: all 0.3s ease-out;
      height: 443px;
      width: 100%;
      display: flex;
      align-items: center;
    }

    &--after {
      position: absolute;
      visibility: hidden;
      width: 0;
      height: 0;
    }

    &-background {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;
    }

    &-visual {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 285px;
      padding-inline: $grid-base;
    }
  }

  &__arrow-symbol {
    position: relative;
    display: inline-flex;
    gap: 2px;
    bottom: 5px;
    &::before {
      content: '';
      border-top: 1.5px solid var(--color-font);
      position: relative;
      align-self: center;
      min-width: 0;
      width: auto;

      transition: min-width 0.3s ease-out;
      will-change: min-width;
    }

    .arrow {
      position: relative;
      width: 0.7em;
      height: 0.7em;
    }
  }
}
</style>
